import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays, isToday, parse } from 'date-fns';
import { tr } from 'date-fns/locale';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import ModuleTour from '../components/Training/ModuleTour';
import { useAuth } from '../App';
import { useIsMobile } from '../hooks/useIsMobile';

// owner/manager randevu ekranında tam yetkili - Settings.js'teki
// FULL_ACCESS_ROLES ve appointments.js'teki backend eşdeğeriyle aynı ikili.
const FULL_ACCESS_ROLES = ['owner', 'manager'];

// Takvim grid'i: 30 dakikalık dilimler, 09:00–24:00
const SLOT_MIN = 30;
const SLOT_PX = 50;
const DAY_START_MIN = 9 * 60;
const DAY_END_MIN = 24 * 60;
const COLUMN_HEIGHT = ((DAY_END_MIN - DAY_START_MIN) / SLOT_MIN) * SLOT_PX;
const slotStarts = Array.from({ length: (DAY_END_MIN - DAY_START_MIN) / SLOT_MIN }, (_, i) => DAY_START_MIN + i * SLOT_MIN);
const hourLabels = Array.from({ length: (DAY_END_MIN - DAY_START_MIN) / 60 }, (_, i) => DAY_START_MIN / 60 + i);

const STATUS_LABELS = [
  { status: 'pending', label: 'Bekliyor' },
  { status: 'confirmed', label: 'Onaylandı' },
  { status: 'in_progress', label: 'İşlemde' },
  { status: 'completed', label: 'Tamamlandı' },
  { status: 'cancelled_by_salon', label: 'İptal' },
  { status: 'no_show', label: 'Gelmedi' }
];

const CANCELLED_STATUSES = ['cancelled_by_customer', 'cancelled_by_salon'];
const DRAGGABLE_STATUSES = ['pending', 'confirmed', 'in_progress'];
const AI_SOURCES = ['ai_agent', 'whatsapp_ai', 'instagram_ai'];
const isAiAppointment = (apt) => AI_SOURCES.includes(apt.source);

// Randevunun görünen gün içindeki dikey konumu (dakika cinsinden, 09:00–24:00 aralığına kırpılmış)
const getEventSpan = (apt) => {
  const start = new Date(apt.scheduled_at);
  let end = apt.end_at ? new Date(apt.end_at) : null;
  if (!end || isNaN(end.getTime()) || end <= start) {
    end = new Date(start.getTime() + (apt.duration_min || 60) * 60000);
  }
  const startMin = start.getHours() * 60 + start.getMinutes();
  // Gün aşan kayıtlar (örn. otel rezervasyonu) görünen günün sonuna kırpılır
  const endMin = isSameDay(end, start) ? end.getHours() * 60 + end.getMinutes() : DAY_END_MIN;
  const clampedStart = Math.min(Math.max(startMin, DAY_START_MIN), DAY_END_MIN - 15);
  const clampedEnd = Math.min(Math.max(endMin, clampedStart + 15), DAY_END_MIN);
  return {
    startMin: clampedStart,
    endMin: clampedEnd,
    clippedTop: startMin < DAY_START_MIN,
    clippedBottom: endMin > DAY_END_MIN,
    realStart: start,
    realEnd: end
  };
};

// Aynı sütunda çakışan randevuları yan yana şeritlere dağıtır
const computeLanes = (events) => {
  const items = events
    .map(e => ({ e, span: getEventSpan(e) }))
    .sort((a, b) => a.span.startMin - b.span.startMin || b.span.endMin - a.span.endMin);
  const result = new Map();
  let cluster = [];
  let clusterEnd = -1;
  const flush = () => {
    const laneEnds = [];
    cluster.forEach(it => {
      let lane = laneEnds.findIndex(endMin => endMin <= it.span.startMin);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(it.span.endMin); }
      else { laneEnds[lane] = it.span.endMin; }
      it.lane = lane;
    });
    cluster.forEach(it => result.set(it.e.id, { lane: it.lane, laneCount: laneEnds.length }));
    cluster = [];
  };
  items.forEach(it => {
    if (cluster.length > 0 && it.span.startMin >= clusterEnd) flush();
    cluster.push(it);
    clusterEnd = cluster.length === 1 ? it.span.endMin : Math.max(clusterEnd, it.span.endMin);
  });
  if (cluster.length > 0) flush();
  return result;
};

function Calendar({ industry = 'beauty_salon' }) {
  const isMobile = useIsMobile();
  const routerNavigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isFullAccess = FULL_ACCESS_ROLES.includes(user?.role);
  // Sidebar.js'teki isModuleEnabled ile AYNI "boşsa serbest" kuralı: owner bu
  // personelin izinlerini hiç özelleştirmemişse (permissions boş/yok) kısıtlama
  // yok - backend appointments.js'teki getAppointmentPermissions ile birebir aynı.
  const appointmentPerms = useMemo(() => {
    const perms = user?.permissions;
    if (!Array.isArray(perms) || perms.length === 0) return { canEdit: true, canDelete: true };
    return { canEdit: perms.includes('edit_appointments'), canDelete: perms.includes('delete_appointments') };
  }, [user]);
  const [appointments, setAppointments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  // Secilen gorunum (Gun/Hafta/Ay/Liste/Salon) tarayicida hatirlanir - sayfa
  // yenilenince veya sonraki oturumda hep "Gun"e donmemesi icin.
  const [view, setView] = useState(() => {
    const saved = localStorage.getItem('calendar_view');
    if (saved) return saved;
    return window.innerWidth <= 768 ? 'list' : 'day';
  });

  useEffect(() => {
    localStorage.setItem('calendar_view', view);
  }, [view]);
  const [showModal, setShowModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  const [visibleStaffIds, setVisibleStaffIds] = useState([]);
  const [staffSearchQuery, setStaffSearchQuery] = useState('');
  const [selectedStaffRole, setSelectedStaffRole] = useState('');
  const [onlyShowWithAppointments, setOnlyShowWithAppointments] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [showOnlyAI, setShowOnlyAI] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [customerSaving, setCustomerSaving] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState({ first_name: '', last_name: '', phone: '', email: '' });

  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  const [blocks, setBlocks] = useState([]);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockSaving, setBlockSaving] = useState(false);
  const [newBlock, setNewBlock] = useState({ staff_id: '', start_at: '', end_at: '', reason: '' });

  const [newAppointment, setNewAppointment] = useState({
    customer_id: '',
    staff_id: '',
    service_id: '',
    product_id: '',
    fitting_type: '',
    scheduled_at: '',
    notes: ''
  });

  // Danışanın geçmiş randevuları (detay modalı ve yeni randevu formu için)
  const [customerHistory, setCustomerHistory] = useState(null);

  // Ön görüşmeden gelindiyse: randevu kaydedilince talep 'scheduled' yapılır
  const [preConsultationId, setPreConsultationId] = useState(null);
  const [preConsultationName, setPreConsultationName] = useState('');

  // Çoklu hizmet satırları: her satır peş peşe ayrı bir randevu bloğu olur
  const [serviceRows, setServiceRows] = useState([{ service_id: '', staff_id: '' }]);
  const [batchConflictIndex, setBatchConflictIndex] = useState(null);

  // Birden fazla hizmet seçildiğinde zincirin özeti (toplam süre/saat/fiyat)
  const chainSummary = useMemo(() => {
    if (industry === 'bridal_shop') return null;
    const rows = serviceRows.filter(r => r.service_id);
    if (rows.length < 2 || !newAppointment.scheduled_at) return null;
    let totalMin = 0, totalPrice = 0;
    rows.forEach(r => {
      const s = services.find(sv => String(sv.id) === String(r.service_id));
      if (s) {
        totalMin += s.duration_min || 30;
        totalPrice += Number(s.discount_price || s.price || 0);
      }
    });
    const start = new Date(newAppointment.scheduled_at);
    if (isNaN(start.getTime())) return null;
    const end = new Date(start.getTime() + totalMin * 60000);
    return { count: rows.length, totalMin, start, end, totalPrice, crossesMidnight: !isSameDay(start, end) };
  }, [serviceRows, newAppointment.scheduled_at, services, industry]);

  const fetchCustomerHistory = async (customerId) => {
    if (!customerId) { setCustomerHistory(null); return; }
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.get(`/api/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerHistory(res.data?.appointments || []);
    } catch {
      // Yetki yoksa (403) veya hata olursa panel sessizce gizlenir
      setCustomerHistory(null);
    }
  };

  const labels = useMemo(() => {
    switch(industry) {
      case 'dental_clinic':
        return { staff: 'Doktor / Diş Hekimi', service: 'Tedavi / Hizmet', staffIcon: '🦷', serviceIcon: '🩺' };
      case 'clinic':
        return { staff: 'Doktor / Uzman Hekim', service: 'Bölüm / Poliklinik', staffIcon: '🩺', serviceIcon: '🏥' };
      case 'dusakabin':
        return { staff: 'Usta / Teknisyen', service: 'Keşif / Montaj İşlemi', staffIcon: '🛠️', serviceIcon: '🚿' };
      case 'hotel':
        return { staff: 'Resepsiyonist / Görevli', service: 'Oda / Hizmet Türü', staffIcon: '🛎️', serviceIcon: '🏨' };
      case 'health_tourism':
        return { staff: 'Danışman / Temsilci', service: 'Tedavi / Paket', staffIcon: '✈️', serviceIcon: '🏥' };
      case 'automotive':
        return { staff: 'Usta / Teknisyen', service: 'Bakım / Servis İşlemi', staffIcon: '🔧', serviceIcon: '🚗' };
      case 'veterinary':
        return { staff: 'Veteriner Hekim', service: 'Tedavi / Aşı', staffIcon: '🐾', serviceIcon: '🐱' };
      case 'ecommerce':
        return { staff: 'Müşteri Temsilcisi', service: 'Destek / Talep Türü', staffIcon: '💬', serviceIcon: '🛒' };
      case 'bridal_shop':
        return { staff: 'Terzi / Temsilci', service: 'Hizmet / Prova Ürünü', staffIcon: '📏', serviceIcon: '👗' };
      default:
        return { staff: 'Personel', service: 'Hizmet', staffIcon: '👩‍💼', serviceIcon: '💅' };
    }
  }, [industry]);

  useEffect(() => {
    fetchData();
  }, [industry]);

  useEffect(() => {
    if (staff.length > 0 && visibleStaffIds.length === 0) {
      setVisibleStaffIds(staff.map(s => s.id));
    }
  }, [staff]);

  useEffect(() => {
    if (customerSearchQuery.trim().length < 2) { setCustomerSearchResults([]); return; }
    const token = localStorage.getItem('crm_token');
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`/api/customers?search=${encodeURIComponent(customerSearchQuery)}&limit=15`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCustomerSearchResults(res.data?.data || []);
      } catch {
        setCustomerSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearchQuery]);

  useEffect(() => {
    fetchAppointments();
    fetchBlocks();
  }, [currentDate, view]);

  // Yeni randevu formunda müşteri seçilince geçmiş işlemleri getir
  useEffect(() => {
    if (showModal && !selectedEvent && industry !== 'bridal_shop') {
      fetchCustomerHistory(newAppointment.customer_id || null);
    }
  }, [newAppointment.customer_id, showModal]);

  // Ön Görüşme ekranından "Randevulandı" ile gelindiyse formu önceden doldur
  useEffect(() => {
    const pc = location.state?.preConsultation;
    if (!pc) return;
    setPreConsultationId(pc.id);
    setPreConsultationName([pc.caller_name, pc.phone].filter(Boolean).join(' · '));

    const preferred = pc.preferred_date ? new Date(pc.preferred_date) : null;
    const validPreferred = preferred && !isNaN(preferred.getTime()) ? preferred : null;
    setNewAppointment(prev => ({
      ...prev,
      customer_id: pc.customer_id || '',
      scheduled_at: validPreferred
        ? format(validPreferred, "yyyy-MM-dd'T'HH:mm")
        : format(new Date(), "yyyy-MM-dd'T'10:00"),
      notes: pc.notes || ''
    }));
    if (validPreferred) setCurrentDate(validPreferred);

    if (pc.customer_id) {
      setCustomerSearchQuery([pc.caller_name, pc.phone].filter(Boolean).join(' - '));
    } else {
      // Telefonla mevcut müşteri aranır; yoksa "Yeni Ekle" formu hazır gelir
      setCustomerSearchQuery(pc.phone || pc.caller_name || '');
      const parts = (pc.caller_name || '').trim().split(/\s+/).filter(Boolean);
      setNewCustomerForm({
        first_name: parts.length > 1 ? parts.slice(0, -1).join(' ') : (parts[0] || ''),
        last_name: parts.length > 1 ? parts[parts.length - 1] : '',
        phone: pc.phone || '',
        email: ''
      });
    }

    setServiceRows([{ service_id: '', staff_id: isFullAccess ? '' : (user?.id || '') }]);
    setBatchConflictIndex(null);
    setSelectedEvent(null);
    setIsEditing(false);
    setShowModal(true);
    // Geri/yenile ile modalın tekrar açılmaması için navigasyon state'i temizlenir
    window.history.replaceState({}, '');
  }, [location.state]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      if (industry !== 'bridal_shop') {
        const [staffRes, servicesRes] = await Promise.all([
          axios.get('/api/staff', { headers: { Authorization: `Bearer ${token}` } }),
          axios.get('/api/services', { headers: { Authorization: `Bearer ${token}` } })
        ]);
        // show_in_calendar === false olarak isaretlenmis personel (ornegin
        // muhasebe/resepsiyon rolu) Randevular ekraninda hic sutun olarak
        // gorunmemeli - Ayarlar > Personel / Personel Yonetimi'nden ayarlanir.
        setStaff(staffRes.data.filter(s => s.show_in_calendar !== 0 && s.show_in_calendar !== false));
        setServices(servicesRes.data);
      } else {
        const [productsRes] = await Promise.all([
          axios.get('/api/products', { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setServices(productsRes.data || []);
      }
    } catch (error) {
      console.error('Fetch data error:', error);
    }
  };

  const fetchAppointments = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      let startDate, endDate;
      
      if (view === 'month') {
        startDate = format(startOfMonth(currentDate), 'yyyy-MM-dd');
        endDate = format(endOfMonth(currentDate), 'yyyy-MM-dd');
      } else if (view === 'week') {
        startDate = format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        endDate = format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      } else {
        startDate = format(currentDate, 'yyyy-MM-dd');
        endDate = format(addDays(currentDate, 1), 'yyyy-MM-dd');
      }
      
      const response = await axios.get(`/api/appointments?start_date=${startDate}&end_date=${endDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAppointments(response.data);
    } catch (error) {
      console.error('Fetch appointments error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlocks = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      let startDate, endDate;

      if (view === 'month') {
        startDate = format(startOfMonth(currentDate), 'yyyy-MM-dd');
        endDate = format(endOfMonth(currentDate), 'yyyy-MM-dd');
      } else if (view === 'week') {
        startDate = format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        endDate = format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      } else {
        startDate = format(currentDate, 'yyyy-MM-dd');
        endDate = format(currentDate, 'yyyy-MM-dd');
      }

      const response = await axios.get(`/api/schedule-blocks?start_date=${startDate}&end_date=${endDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlocks(response.data);
    } catch (error) {
      console.error('Fetch schedule blocks error:', error);
    }
  };

  const getBlocksForColumn = (day, staffId) => {
    const dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(day);
    dayEnd.setHours(24, 0, 0, 0);
    return blocks.filter(b => {
      if (b.staff_id && staffId && b.staff_id !== staffId) return false;
      const bStart = new Date(b.start_at);
      const bEnd = new Date(b.end_at);
      return bStart < dayEnd && bEnd > dayStart;
    });
  };

  const getBlockSpan = (b, day) => {
    const bStart = new Date(b.start_at);
    const bEnd = new Date(b.end_at);
    const startMin = isSameDay(bStart, day) ? bStart.getHours() * 60 + bStart.getMinutes() : 0;
    const endMin = isSameDay(bEnd, day) ? bEnd.getHours() * 60 + bEnd.getMinutes() : DAY_END_MIN;
    const clampedStart = Math.max(startMin, DAY_START_MIN);
    const clampedEnd = Math.min(endMin, DAY_END_MIN);
    if (clampedEnd <= clampedStart) return null;
    return { startMin: clampedStart, endMin: clampedEnd };
  };

  const handleCreateBlock = async (e) => {
    e.preventDefault();
    try {
      setBlockSaving(true);
      const token = localStorage.getItem('crm_token');
      await axios.post('/api/schedule-blocks', {
        staff_id: newBlock.staff_id || null,
        start_at: newBlock.start_at,
        end_at: newBlock.end_at,
        reason: newBlock.reason,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShowBlockModal(false);
      setNewBlock({ staff_id: '', start_at: '', end_at: '', reason: '' });
      fetchBlocks();
    } catch (error) {
      alert(error.response?.data?.error || 'Saat kapatılamadı');
    } finally {
      setBlockSaving(false);
    }
  };

  const handleDeleteBlock = async (block) => {
    if (!window.confirm(`"${block.reason || 'Kapalı saat'}" kaldırılsın mı?`)) return;
    try {
      const token = localStorage.getItem('crm_token');
      await axios.delete(`/api/schedule-blocks/${block.id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchBlocks();
    } catch (error) {
      alert('Kapatma kaldırılamadı');
    }
  };

  const handleSaveAppointment = async (e) => {
    e.preventDefault();
    if (!newAppointment.customer_id) {
      toast.error('Lütfen listeden bir müşteri seçin');
      return;
    }
    const isBridal = industry === 'bridal_shop';
    const rows = isBridal ? [] : serviceRows.filter(r => r.service_id && r.staff_id);
    if (!isBridal && rows.length === 0) {
      toast.error('En az bir hizmet ve personel seçin');
      return;
    }
    const wasEditing = isEditing;
    try {
      const token = localStorage.getItem('crm_token');
      const headers = { Authorization: `Bearer ${token}` };
      setBatchConflictIndex(null);

      if (wasEditing) {
        const payload = { ...newAppointment };
        if (!isBridal) {
          payload.service_id = rows[0].service_id;
          payload.staff_id = rows[0].staff_id;
        }
        await axios.put(`/api/appointments/${selectedEvent.id}`, payload, { headers });
        setIsEditing(false);
        setSelectedEvent(null);
      } else if (!isBridal && rows.length > 1) {
        await axios.post('/api/appointments/batch', {
          customer_id: newAppointment.customer_id,
          scheduled_at: newAppointment.scheduled_at,
          notes: newAppointment.notes,
          source: preConsultationId ? 'pre_consultation' : 'manual',
          items: rows.map(r => ({ service_id: r.service_id, staff_id: r.staff_id }))
        }, { headers });
      } else {
        const payload = { ...newAppointment };
        if (!isBridal) {
          payload.service_id = rows[0].service_id;
          payload.staff_id = rows[0].staff_id;
        }
        if (preConsultationId) payload.source = 'pre_consultation';
        await axios.post('/api/appointments', payload, { headers });
      }

      // Ön görüşmeden gelindiyse talep artık gerçekten randevulu
      if (!wasEditing && preConsultationId) {
        try {
          await axios.put(`/api/pre-consultations/${preConsultationId}`, { status: 'scheduled' }, { headers });
          toast.success('Ön görüşme talebi "Randevulu" olarak işaretlendi');
        } catch {
          toast.warning('Randevu oluştu ancak ön görüşme durumu güncellenemedi');
        }
        setPreConsultationId(null);
        setPreConsultationName('');
      }

      toast.success(wasEditing
        ? 'Randevu güncellendi'
        : (rows.length > 1 ? `${rows.length} randevu peş peşe oluşturuldu` : 'Randevu oluşturuldu'));
      setShowModal(false);
      fetchAppointments();
      setNewAppointment({
        customer_id: '',
        staff_id: '',
        service_id: '',
        product_id: '',
        fitting_type: '',
        scheduled_at: '',
        notes: ''
      });
      setServiceRows([{ service_id: '', staff_id: isFullAccess ? '' : (user?.id || '') }]);
      setCustomerSearchQuery('');
      setCustomerSearchResults([]);
    } catch (error) {
      const idx = error.response?.data?.conflict_index;
      if (idx !== undefined && idx !== null) setBatchConflictIndex(idx);
      toast.error(error.response?.data?.error || (wasEditing ? 'Randevu güncellenemedi' : 'Randevu oluşturulamadı'));
    }
  };

  const handleQuickAddCustomer = async (e) => {
    e.preventDefault();
    if (!newCustomerForm.first_name || !newCustomerForm.phone) return;
    try {
      setCustomerSaving(true);
      const token = localStorage.getItem('crm_token');
      const res = await axios.post('/api/customers', newCustomerForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.id) {
        setNewAppointment({ ...newAppointment, customer_id: res.data.id });
        setCustomerSearchQuery(`${res.data.first_name || newCustomerForm.first_name} ${res.data.last_name || newCustomerForm.last_name || ''} - ${res.data.phone || newCustomerForm.phone}`);
      }
      setShowNewCustomerModal(false);
      setNewCustomerForm({ first_name: '', last_name: '', phone: '', email: '' });
    } catch (error) {
      alert(error.response?.data?.error || 'Müşteri eklenemedi');
    } finally {
      setCustomerSaving(false);
    }
  };

  const handleUpdateStatus = async (status) => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.put(`/api/appointments/${selectedEvent.id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEvent(prev => ({ ...prev, ...res.data }));
      fetchAppointments();
      if (status !== 'completed') {
        setShowModal(false);
      }
    } catch (error) {
      alert('Güncelleme başarısız');
    }
  };

  const handleUpdateFields = async (fields) => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.put(`/api/appointments/${selectedEvent.id}`, fields, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEvent(prev => ({ ...prev, ...res.data }));
      fetchAppointments();
    } catch (error) {
      console.error('Failed to update fields:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: { bg: 'rgba(245, 158, 11, 0.08)', text: '#fbbf24', border: '#fbbf24' },
      confirmed: { bg: 'rgba(59, 130, 246, 0.08)', text: '#60a5fa', border: '#3b82f6' },
      in_progress: { bg: 'rgba(139, 92, 246, 0.08)', text: '#a78bfa', border: '#8b5cf6' },
      completed: { bg: 'rgba(16, 185, 129, 0.08)', text: '#34d399', border: '#10b981' },
      cancelled_by_customer: { bg: 'rgba(239, 68, 68, 0.08)', text: '#f87171', border: '#ef4444' },
      cancelled_by_salon: { bg: 'rgba(239, 68, 68, 0.08)', text: '#f87171', border: '#ef4444' },
      no_show: { bg: 'rgba(107, 114, 128, 0.08)', text: '#9ca3af', border: '#6b7280' }
    };
    return colors[status] || colors.pending;
  };

  const days = useMemo(() => {
    if (view === 'month') {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const start = startOfWeek(monthStart, { weekStartsOn: 1 });
      const end = endOfWeek(monthEnd, { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end });
    } else if (view === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end });
    } else {
      return [currentDate];
    }
  }, [currentDate, view]);

  const getAppointmentsForDay = (day) => {
    let filtered = appointments.filter(apt => isSameDay(new Date(apt.scheduled_at), day));
    if (!showCancelled) {
      filtered = filtered.filter(apt => !CANCELLED_STATUSES.includes(apt.status));
    }
    if (showOnlyAI) {
      filtered = filtered.filter(isAiAppointment);
    }
    if (staff.length > 0) {
      if (visibleStaffIds.length > 0) {
        filtered = filtered.filter(apt => visibleStaffIds.includes(apt.staff_id));
      } else {
        return [];
      }
    }
    return filtered;
  };

  const hiddenCancelledCount = useMemo(
    () => appointments.filter(apt => CANCELLED_STATUSES.includes(apt.status)).length,
    [appointments]
  );

  const aiAppointmentCount = useMemo(
    () => appointments.filter(isAiAppointment).length,
    [appointments]
  );

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (showOnlyAI) count++;
    if (showCancelled) count++;
    if (staffSearchQuery.trim()) count++;
    if (selectedStaffRole) count++;
    if (onlyShowWithAppointments) count++;
    if (staff.length > 0 && visibleStaffIds.length < staff.length) count++;
    return count;
  }, [showOnlyAI, showCancelled, staffSearchQuery, selectedStaffRole, onlyShowWithAppointments, staff, visibleStaffIds]);

  const selectableStaff = useMemo(() => {
    return staff.filter(st => {
      if (staffSearchQuery) {
        const fullName = `${st.first_name} ${st.last_name}`.toLowerCase();
        if (!fullName.includes(staffSearchQuery.toLowerCase())) {
          return false;
        }
      }
      if (selectedStaffRole && st.role !== selectedStaffRole) {
        return false;
      }
      return true;
    });
  }, [staff, staffSearchQuery, selectedStaffRole]);

  const filteredStaff = useMemo(() => {
    return staff.filter(st => {
      if (staffSearchQuery) {
        const fullName = `${st.first_name} ${st.last_name}`.toLowerCase();
        if (!fullName.includes(staffSearchQuery.toLowerCase())) {
          return false;
        }
      }
      if (selectedStaffRole && st.role !== selectedStaffRole) {
        return false;
      }
      if (visibleStaffIds.length > 0 && !visibleStaffIds.includes(st.id)) {
        return false;
      }
      if (onlyShowWithAppointments) {
        const hasApt = appointments.some(apt => isSameDay(new Date(apt.scheduled_at), currentDate) && apt.staff_id === st.id);
        if (!hasApt) return false;
      }
      return true;
    });
  }, [staff, staffSearchQuery, selectedStaffRole, visibleStaffIds, onlyShowWithAppointments, appointments, currentDate]);

  const navigatePeriod = (direction) => {
    if (view === 'month') {
      setCurrentDate(direction === 'next' ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
    } else if (view === 'week') {
      setCurrentDate(direction === 'next' ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1));
    } else {
      setCurrentDate(direction === 'next' ? addDays(currentDate, 1) : subDays(currentDate, 1));
    }
  };

  const handleSlotClick = (day, minutes, staffId = null) => {
    const date = new Date(day);
    date.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    // Personel (owner/manager dışı roller) sadece kendisi için randevu girebilir -
    // hangi personelin sütununa/saatine tıklanırsa tıklansın kendi id'sine kilitlenir.
    const effectiveStaffId = isFullAccess ? staffId : (staffId ? (user?.id || staffId) : null);
    setNewAppointment({
      ...newAppointment,
      scheduled_at: format(date, "yyyy-MM-dd'T'HH:mm:ss"),
      ...(effectiveStaffId ? { staff_id: effectiveStaffId } : {})
    });
    if (effectiveStaffId) {
      setServiceRows(prev => [{ ...prev[0], staff_id: effectiveStaffId }, ...prev.slice(1)]);
    }
    setBatchConflictIndex(null);
    setSelectedEvent(null);
    setShowModal(true);
  };

  const handleEventDragStart = (e, apt) => {
    e.dataTransfer.setData('text/appointment-id', String(apt.id));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleEventDrop = async (e, day, minutes, staffId = null) => {
    e.preventDefault();
    const id = parseInt(e.dataTransfer.getData('text/appointment-id'), 10);
    if (!id) return;
    const apt = appointments.find(a => a.id === id);
    if (!apt) return;

    const date = new Date(day);
    date.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    const prevStart = new Date(apt.scheduled_at);
    const staffChanged = staffId && staffId !== apt.staff_id;
    if (prevStart.getTime() === date.getTime() && !staffChanged) return;

    const body = { scheduled_at: format(date, "yyyy-MM-dd'T'HH:mm:ss") };
    if (staffChanged) body.staff_id = staffId;

    // Optimistic update: blok hemen taşınır, hata olursa geri alınır
    const snapshot = appointments;
    const delta = date.getTime() - prevStart.getTime();
    setAppointments(appointments.map(a => a.id === id ? {
      ...a,
      scheduled_at: format(date, "yyyy-MM-dd'T'HH:mm:ss"),
      end_at: a.end_at ? format(new Date(new Date(a.end_at).getTime() + delta), "yyyy-MM-dd'T'HH:mm:ss") : a.end_at,
      ...(staffChanged ? { staff_id: staffId } : {})
    } : a));

    try {
      const token = localStorage.getItem('crm_token');
      await axios.put(`/api/appointments/${id}`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Randevu taşındı');
      fetchAppointments();
    } catch (err) {
      setAppointments(snapshot);
      toast.error(err.response?.data?.error || 'Randevu taşınamadı');
    }
  };

  const handleEventClick = async (apt) => {
    setSelectedEvent(apt); // set temporary data while loading
    setShowModal(true);
    fetchCustomerHistory(apt.customer_id);
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.get(`/api/appointments/${apt.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEvent(res.data);
    } catch (err) {
      console.error('Failed to load detailed appointment data:', err);
    }
  };

  const formatDateRange = () => {
    if (view === 'month') {
      return format(currentDate, 'MMMM yyyy', { locale: tr });
    } else if (view === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(start, 'd MMM', { locale: tr })} - ${format(end, 'd MMM yyyy', { locale: tr })}`;
    } else {
      return format(currentDate, 'd MMMM yyyy, EEEE', { locale: tr });
    }
  };

  // Bir gün/personel sütununun gövdesi: 30 dk arka plan hücreleri + blok katmanı + orantısal randevu katmanı
  // columnColor: SADECE Salon Görünümü'nde kullanılır - randevu bloğu, o sütunun
  // pastel rengiyle (durum rengine göre değil) boyanır, referans tasarımla birebir.
  const renderDayColumnBody = (day, columnAppointments, staffId, columnColor) => {
    const lanes = computeLanes(columnAppointments);
    const columnBlocks = getBlocksForColumn(day, staffId);
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    // Su anki saat cizgisi SADECE Salon Görünümü'ne ozel - Orijinal Görünüm eski
    // haliyle (cizgisiz) kalir, ikisi kasitli olarak farkli gorunur.
    const showNowLine = view === 'salon' && isToday(day) && nowMinutes >= DAY_START_MIN && nowMinutes <= DAY_END_MIN;
    return (
      <div className="day-slots" style={{ height: `${COLUMN_HEIGHT}px` }}>
        {showNowLine && (
          <div
            className="current-time-line"
            style={{ top: `${((nowMinutes - DAY_START_MIN) / SLOT_MIN) * SLOT_PX}px` }}
          />
        )}
        {slotStarts.map(min => (
          <div
            key={min}
            className={`half-slot ${(min + SLOT_MIN) % 60 === 0 ? 'hour-end' : ''}`}
            onClick={() => handleSlotClick(day, min, staffId)}
            onDragOver={e => e.preventDefault()}
            onDragEnter={e => e.currentTarget.classList.add('drop-hover')}
            onDragLeave={e => e.currentTarget.classList.remove('drop-hover')}
            onDrop={e => { e.currentTarget.classList.remove('drop-hover'); handleEventDrop(e, day, min, staffId); }}
          />
        ))}
        {columnBlocks.map(b => {
          const span = getBlockSpan(b, day);
          if (!span) return null;
          return (
            <div
              key={`block-${b.id}`}
              className={`block-overlay ${view === 'salon' ? 'block-overlay-salon' : ''}`}
              style={{
                top: `${((span.startMin - DAY_START_MIN) / SLOT_MIN) * SLOT_PX}px`,
                height: `${((span.endMin - span.startMin) / SLOT_MIN) * SLOT_PX}px`
              }}
              title={b.reason || 'Kapalı'}
              onClick={() => handleDeleteBlock(b)}
            >
              🔒 {b.reason || 'Kapalı'}
            </div>
          );
        })}
        <div className="events-layer">
          {columnAppointments.map(apt => {
            const span = getEventSpan(apt);
            const lane = lanes.get(apt.id) || { lane: 0, laneCount: 1 };
            const sColor = getStatusColor(apt.status);
            const isCancelled = CANCELLED_STATUSES.includes(apt.status);
            const draggable = DRAGGABLE_STATUSES.includes(apt.status);
            const blockHeight = Math.max(((span.endMin - span.startMin) / SLOT_MIN) * SLOT_PX - 2, 22);
            // Kisa randevularda (ör. 15-20 dk) 3 satir (saat+ad+hizmet) sigmiyor —
            // once hizmet satiri gizlenir. Blok cok kisaysa (<30px, ~18 dk altindaki
            // randevularda min. 22px yukseklik zorlanir) TEK satira musteri adi
            // yaziliyor - saat degil, cunku "kiminle" bilgisi bir bakista "saat kaçta"
            // bilgisinden daha kritik (saat zaten bloğun dikey konumundan/tikla-goruntule
            // ile okunabiliyor). Eskiden bu durumda sadece saat gosterilip ad tamamen
            // gizleniyordu - Liste görünümünde/detayda görünen ad, Orijinal Görünüm'de
            // hiç gözükmüyordu.
            const showService = blockHeight >= 50;
            const showBothLines = blockHeight >= 30;
            return (
              <div
                key={apt.id}
                className={`calendar-event ${isCancelled ? 'cancelled' : ''}`}
                draggable={draggable}
                onDragStart={draggable ? (e) => handleEventDragStart(e, apt) : undefined}
                style={{
                  top: `${((span.startMin - DAY_START_MIN) / SLOT_MIN) * SLOT_PX}px`,
                  height: `${blockHeight}px`,
                  left: `calc(${(lane.lane / lane.laneCount) * 100}% + 2px)`,
                  width: `calc(${100 / lane.laneCount}% - 5px)`,
                  background: columnColor || `linear-gradient(${sColor.bg}, ${sColor.bg}), var(--bg-card)`,
                  borderLeft: columnColor ? 'none' : `4px solid ${sColor.border}`,
                  color: columnColor ? '#2b2b2b' : sColor.text
                }}
                onClick={(e) => { e.stopPropagation(); handleEventClick(apt); }}
              >
                <div className="event-content">
                  {showBothLines ? (
                    <>
                      <span className="event-time">
                        {span.clippedTop && '▲ '}
                        {format(span.realStart, 'HH:mm')}–{format(span.realEnd, 'HH:mm')}
                        {span.clippedBottom && ' ▼'}
                      </span>
                      <span className="event-title">{isAiAppointment(apt) && '🤖 '}{apt.customer_first_name} {apt.customer_last_name}</span>
                    </>
                  ) : (
                    <span className="event-title">{isAiAppointment(apt) && '🤖 '}{apt.customer_first_name} {apt.customer_last_name}</span>
                  )}
                  {showService && <span className="event-service">{apt.service_name}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <ModuleTour moduleKey="calendar" />
      <div className="page-header" data-tour="cal-header" style={{ flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 className="page-title">Randevular</h1>
          <p className="page-subtitle">Tüm randevularınızı takvim üzerinde yönetin</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {industry !== 'bridal_shop' && (
            <button className="btn btn-secondary" onClick={() => {
              const start = new Date(currentDate);
              start.setHours(9, 0, 0, 0);
              const end = new Date(currentDate);
              end.setHours(10, 0, 0, 0);
              setNewBlock({
                staff_id: '',
                start_at: format(start, "yyyy-MM-dd'T'HH:mm"),
                end_at: format(end, "yyyy-MM-dd'T'HH:mm"),
                reason: ''
              });
              setShowBlockModal(true);
            }}>
              <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
              Saat Kapat
            </button>
          )}
          <button className="btn btn-primary" onClick={() => {
            setNewAppointment({
              customer_id: '',
              staff_id: '',
              service_id: '',
              product_id: '',
              scheduled_at: format(new Date(), "yyyy-MM-dd'T'09:00"),
              notes: ''
            });
            setServiceRows([{ service_id: '', staff_id: isFullAccess ? '' : (user?.id || '') }]);
            setBatchConflictIndex(null);
            setCustomerSearchQuery('');
            setCustomerSearchResults([]);
            setSelectedEvent(null);
            setShowModal(true);
          }}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            {view === 'salon' ? 'Yeni Adisyon' : 'Yeni Randevu'}
          </button>
        </div>
      </div>

      <div className="modern-calendar">
        <div className="calendar-header" data-tour="cal-toolbar">
          <div className="calendar-nav">
            <button className="nav-btn" onClick={() => navigatePeriod('prev')}>
              <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            </button>
            <button className="today-btn" onClick={() => setCurrentDate(new Date())}>Bugün</button>
            <button className="nav-btn" onClick={() => navigatePeriod('next')}>
              <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
            </button>
            <h2 className="calendar-title">{formatDateRange()}</h2>
          </div>
          <div className="view-tabs">
            <button className={`view-tab ${view === 'day' ? 'active' : ''}`} onClick={() => setView('day')}>
              {isMobile ? 'Gün' : 'Orijinal Görünüm'}
            </button>
            <button className={`view-tab ${view === 'week' ? 'active' : ''}`} onClick={() => setView('week')}>Hafta</button>
            <button className={`view-tab ${view === 'month' ? 'active' : ''}`} onClick={() => setView('month')}>Ay</button>
            <button className={`view-tab ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')}>Liste</button>
            <button className={`view-tab ${view === 'salon' ? 'active' : ''}`} onClick={() => setView('salon')}>
              {isMobile ? 'Salon' : 'Salon Görünümü'}
            </button>
          </div>
        </div>

        {/* Mobilde Kompakt Filtre / Lejant Aç-Kapa Çubuğu */}
        {isMobile && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 14px',
            background: 'rgba(255, 255, 255, 0.02)',
            borderBottom: '1px solid var(--border)',
            gap: '8px'
          }}>
            <button
              type="button"
              onClick={() => setMobileFiltersOpen(v => !v)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '8px',
                background: mobileFiltersOpen ? 'rgba(var(--primary-rgb), 0.15)' : 'rgba(255, 255, 255, 0.05)',
                border: `1px solid ${mobileFiltersOpen ? 'var(--primary)' : 'rgba(255, 255, 255, 0.1)'}`,
                color: mobileFiltersOpen ? 'var(--primary)' : 'var(--text)',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              <span>⚙️ Filtreler & Lejant</span>
              {activeFilterCount > 0 && (
                <span style={{
                  background: 'var(--primary)',
                  color: '#000',
                  fontSize: '10px',
                  fontWeight: 800,
                  borderRadius: '999px',
                  padding: '1px 6px'
                }}>
                  {activeFilterCount}
                </span>
              )}
              <span style={{ fontSize: '9px', opacity: 0.7 }}>{mobileFiltersOpen ? '▲ Kapat' : '▼ Aç'}</span>
            </button>
            {industry !== 'bridal_shop' && staff.length > 0 && (
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                {filteredStaff.length} / {staff.length} Personel
              </span>
            )}
          </div>
        )}

        {/* Durum Lejantı + İptal Görünürlüğü (Masaüstünde açık, mobilde butonla açılır) */}
        {(!isMobile || mobileFiltersOpen) && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '8px' : '14px',
            flexWrap: 'wrap',
            padding: isMobile ? '10px 14px' : '10px 24px',
            borderBottom: '1px solid var(--border)',
            background: isMobile ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.01)'
          }}>
            {STATUS_LABELS.map(({ status, label }) => {
              const c = getStatusColor(status);
              const isCancelledLegend = status === 'cancelled_by_salon';
              return (
                <span key={status} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: 'var(--text-muted)' }}>
                  <span style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '3px',
                    background: c.border,
                    opacity: isCancelledLegend ? 0.55 : 1,
                    border: isCancelledLegend ? `1px dashed ${c.text}` : 'none',
                    boxSizing: 'border-box'
                  }}></span>
                  {label}
                </span>
              );
            })}
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer', marginLeft: isMobile ? 0 : 'auto' }}>
              <input
                type="checkbox"
                checked={showOnlyAI}
                onChange={e => setShowOnlyAI(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              🤖 Sadece AI Randevuları{aiAppointmentCount > 0 ? ` (${aiAppointmentCount})` : ''}
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showCancelled}
                onChange={e => setShowCancelled(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              İptal edilenleri göster{hiddenCancelledCount > 0 ? ` (${hiddenCancelledCount})` : ''}
            </label>
          </div>
        )}

        {/* Personel Filtreleme Paneli (Masaüstünde açık, mobilde butonla açılır) */}
        {industry !== 'bridal_shop' && staff.length > 0 && (!isMobile || mobileFiltersOpen) && (
          <div className="calendar-filters" style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            padding: isMobile ? '12px 14px' : '16px 24px',
            background: isMobile ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.01)',
            borderBottom: '1px solid var(--border)'
          }}>
            <div style={{ display: 'flex', gap: isMobile ? '10px' : '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: isMobile ? '1 1 100%' : 'none' }}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>🔍 Personel:</span>
                <input 
                  type="text" 
                  className="input" 
                  placeholder="İsim ile ara..." 
                  value={staffSearchQuery} 
                  onChange={e => setStaffSearchQuery(e.target.value)} 
                  style={{ width: isMobile ? '100%' : '160px', padding: '6px 12px' }}
                />
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: isMobile ? '1 1 100%' : 'none' }}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>👔 Görev:</span>
                <select 
                  className="select" 
                  value={selectedStaffRole} 
                  onChange={e => setSelectedStaffRole(e.target.value)}
                  style={{ width: isMobile ? '100%' : '150px', padding: '6px 12px' }}
                >
                  <option value="">Tüm Roller</option>
                  <option value="owner">Sahip</option>
                  <option value="manager">Yönetici</option>
                  <option value="senior_staff">Kıdemli Personel</option>
                  <option value="staff">Personel</option>
                  <option value="therapist">Terapist / Uzman</option>
                  <option value="intern">Stajyer</option>
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  <input 
                    type="checkbox" 
                    checked={onlyShowWithAppointments} 
                    onChange={e => setOnlyShowWithAppointments(e.target.checked)} 
                    style={{ cursor: 'pointer' }}
                  />
                  Sadece Randevusu Olanlar
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', borderTop: '1px dashed var(--border)', paddingTop: '10px' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>Görünürlük:</span>
              <button 
                type="button" 
                className="btn btn-sm" 
                style={{ padding: '4px 8px', fontSize: '11px', background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '4px', color: 'var(--text)' }}
                onClick={() => setVisibleStaffIds(staff.map(s => s.id))}
              >
                Tümünü Göster
              </button>
              <button 
                type="button" 
                className="btn btn-sm" 
                style={{ padding: '4px 8px', fontSize: '11px', background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '4px', color: 'var(--text)' }}
                onClick={() => setVisibleStaffIds([])}
              >
                Tümünü Gizle
              </button>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginLeft: isMobile ? 0 : '8px', width: isMobile ? '100%' : 'auto' }}>
                {selectableStaff.map(st => {
                  const isVisible = visibleStaffIds.includes(st.id);
                  return (
                    <span 
                      key={st.id}
                      onClick={() => {
                        if (isVisible) {
                          setVisibleStaffIds(visibleStaffIds.filter(id => id !== st.id));
                        } else {
                          setVisibleStaffIds([...visibleStaffIds, st.id]);
                        }
                      }}
                      style={{ 
                        cursor: 'pointer', 
                        padding: '4px 10px', 
                        borderRadius: '20px', 
                        fontSize: '11px', 
                        fontWeight: '600',
                        background: isVisible ? st.color_code : 'rgba(255,255,255,0.03)',
                        color: isVisible ? '#fff' : 'var(--text-muted)',
                        border: `1px solid ${isVisible ? st.color_code : 'var(--border)'}`,
                        transition: 'all 0.2s',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: isVisible ? '#fff' : st.color_code }}></span>
                      {st.first_name} {st.last_name}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="calendar-grid" data-tour="cal-main">
          {view === 'month' ? (
            <>
              <div className="weekday-header">
                {['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'].map(day => (
                  <div key={day} className="weekday">{day}</div>
                ))}
              </div>
              <div className="month-grid">
                {days.map(day => {
                  const dayAppointments = getAppointmentsForDay(day);
                  return (
                    <div 
                      key={day.toString()} 
                      className={`month-day ${!isSameMonth(day, currentDate) ? 'other-month' : ''} ${isToday(day) ? 'today' : ''}`}
                      onClick={() => {
                        setCurrentDate(day);
                        setView('day');
                      }}
                    >
                      <span className="day-number">{format(day, 'd')}</span>
                      <div className="day-events">
                        {dayAppointments.slice(0, 3).map(apt => {
                          const color = getStatusColor(apt.status);
                          return (
                            <div 
                              key={apt.id} 
                              className="day-event"
                              style={{ background: color.bg, borderLeftColor: color.border, color: color.text }}
                              onClick={(e) => { e.stopPropagation(); handleEventClick(apt); }}
                            >
                              <span className="event-time">{format(new Date(apt.scheduled_at), 'HH:mm')}</span>
                              <span className="event-name">{apt.customer_first_name} {apt.customer_last_name}</span>
                            </div>
                          );
                        })}
                        {dayAppointments.length > 3 && (
                          <div className="more-events">+{dayAppointments.length - 3} daha</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : view === 'list' ? (
            <div className="appointment-list" style={{ paddingBottom: isMobile ? '80px' : '16px' }}>
              {(() => {
                const dayAppointments = [...getAppointmentsForDay(currentDate)].sort(
                  (a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)
                );
                if (dayAppointments.length === 0) {
                  return <div className="appointment-list-empty">Bu tarihte randevu yok.</div>;
                }
                return dayAppointments.map(apt => {
                  const sColor = getStatusColor(apt.status);
                  const isCancelled = CANCELLED_STATUSES.includes(apt.status);
                  const staffColor = staff.find(s => s.id === apt.staff_id)?.color_code;
                  return (
                    <div
                      key={apt.id}
                      className={`appointment-list-row ${isCancelled ? 'cancelled' : ''}`}
                      onClick={() => handleEventClick(apt)}
                    >
                      <div className="al-time">
                        <span className="al-time-start">{format(new Date(apt.scheduled_at), 'HH:mm')}</span>
                        <span className="al-time-end">{apt.end_at ? format(new Date(apt.end_at), 'HH:mm') : ''}</span>
                      </div>
                      <div className="al-main">
                        <span className="al-customer">{isAiAppointment(apt) && '🤖 '}{apt.customer_first_name} {apt.customer_last_name}</span>
                        <span className="al-service">{apt.service_name || apt.product_name}</span>
                      </div>
                      <div className="al-staff">
                        {staffColor && <span className="al-staff-dot" style={{ background: staffColor }}></span>}
                        {apt.staff_first_name} {apt.staff_last_name}
                      </div>
                      <div className="al-status" style={{ background: sColor.bg, color: sColor.text, borderColor: sColor.border }}>
                        {STATUS_LABELS.find(s => s.status === apt.status)?.label
                          || (isCancelled ? 'İptal' : apt.status)}
                      </div>
                      <div className="al-price">{apt.final_price ? `${apt.final_price} ₺` : ''}</div>
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <>
              <div className={`time-grid ${view === 'salon' ? 'salon-theme' : ''}`}>
                <div className="time-column" style={{ position: 'sticky', left: 0, background: 'var(--bg-card)', zIndex: 10 }}>
                  <div className="day-header" style={{ visibility: 'hidden' }}>
                    <span className="day-name" style={(view === 'day' || view === 'salon') ? { fontSize: '14px', fontWeight: 'bold' } : undefined}>-</span>
                    {view === 'week' && <span className="day-date">-</span>}
                  </div>
                  {hourLabels.map(hour => (
                    <div key={hour} className="time-slot">{hour}:00</div>
                  ))}
                </div>
                {(view === 'day' || view === 'salon') ? (
                  filteredStaff.length > 0 ? filteredStaff.map((st, index) => {
                    const colors = [
                      '#e11d48', '#d97706', '#059669', '#2563eb', '#7c3aed', '#db2777', '#0891b2', '#65a30d'
                    ];
                    // Salon Görünümü'nün referans tasarımıyla birebir eşleşen pastel
                    // paleti - Orijinal Görünüm eski doygun/rastgele paleti aynen korur,
                    // ikisi kasıtlı olarak farklı görünür.
                    const salonColors = [
                      '#f2a8c8', '#f0b955', '#c9b8e6', '#a8d4e0', '#d4508f',
                      '#8298d8', '#a888d0', '#9de0be', '#a8d8e8', '#4a9c94', '#2c7972'
                    ];
                    const isSalon = view === 'salon';
                    const color = isSalon ? salonColors[index % salonColors.length] : colors[index % colors.length];
                    const staffAppointments = getAppointmentsForDay(currentDate).filter(apt => apt.staff_id === st.id);
                    return (
                      <div key={st.id} className="day-column">
                        <div className="day-header" style={{ background: color, borderBottom: 'none', padding: '12px' }}>
                          <span className="day-name" style={{ color: isSalon ? '#2b2b2b' : '#fff', fontSize: '14px', fontWeight: 'bold' }}>{st.first_name} {st.last_name}</span>
                        </div>
                        {renderDayColumnBody(currentDate, staffAppointments, st.id, isSalon ? color : null)}
                      </div>
                    )
                  }) : (
                    <div style={{padding: '20px', color: 'var(--text-muted)'}}>Görüntülenecek personel bulunamadı.</div>
                  )
                ) : (
                  days.map(day => (
                    <div key={day.toString()} className={`day-column ${isToday(day) ? 'today' : ''}`}>
                      <div className="day-header">
                        <span className="day-name">{format(day, 'EEE', { locale: tr })}</span>
                        <span className="day-date">{format(day, 'd')}</span>
                      </div>
                      {renderDayColumnBody(day, getAppointmentsForDay(day), null)}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay appointment-modal-overlay" onClick={() => { setShowModal(false); setIsEditing(false); }} style={isMobile ? { alignItems: 'flex-end', padding: 0 } : {}}>
          <div className="modal appointment-modal" onClick={e => e.stopPropagation()} style={isMobile ? { maxWidth: '100%', width: '100%', borderRadius: '20px 20px 0 0', maxHeight: '92dvh', overflowY: 'auto', margin: 0 } : { maxWidth: '560px' }}>
            <div className="modal-header">
              <h3 className="modal-title">
                {isEditing ? 'Randevu Düzenle' : (selectedEvent ? 'Randevu Detayları' : 'Yeni Randevu Oluştur')}
              </h3>
              <button className="modal-close" onClick={() => { setShowModal(false); setIsEditing(false); }}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            {selectedEvent && !isEditing ? (
              <div className="modal-body">
                <div className="appointment-detail-card">
                  <div className="detail-header">
                    <div className="avatar avatar-lg" style={{ background: 'linear-gradient(135deg, var(--tertiary), #ec4899)' }}>
                      {selectedEvent.customer_first_name[0]}
                    </div>
                    <div className="detail-header-info">
                      <h3>{selectedEvent.customer_first_name} {selectedEvent.customer_last_name}</h3>
                      <p>{selectedEvent.customer_phone}</p>
                      {selectedEvent.customer_id && (
                        <button
                          type="button"
                          onClick={() => routerNavigate(`/customers/${selectedEvent.customer_id}`)}
                          style={{ marginTop: '6px', background: 'none', border: 'none', padding: 0, color: 'var(--tertiary)', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}
                        >
                          👤 Müşteri Kartı →
                        </button>
                      )}
                    </div>
                    <div className={`status-badge status-${selectedEvent.status}`}>
                      {selectedEvent.status === 'pending' && 'Bekliyor'}
                      {selectedEvent.status === 'confirmed' && 'Onaylandı'}
                      {selectedEvent.status === 'in_progress' && 'İşlemde'}
                      {selectedEvent.status === 'completed' && 'Tamamlandı'}
                      {(selectedEvent.status === 'cancelled_by_customer' || selectedEvent.status === 'cancelled_by_salon') && 'İptal'}
                      {selectedEvent.status === 'no_show' && 'Gelmedi'}
                    </div>
                  </div>

                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-icon">{labels.serviceIcon}</span>
                      <div>
                        <span className="detail-label">{labels.service}</span>
                        <span className="detail-value">{selectedEvent.service_name}</span>
                      </div>
                    </div>
                    <div className="detail-item">
                      <span className="detail-icon">{labels.staffIcon}</span>
                      <div>
                        <span className="detail-label">{labels.staff}</span>
                        <span className="detail-value">{selectedEvent.staff_first_name} {selectedEvent.staff_last_name}</span>
                      </div>
                    </div>
                    <div className="detail-item">
                      <span className="detail-icon">📅</span>
                      <div>
                        <span className="detail-label">Tarih</span>
                        <span className="detail-value">{format(new Date(selectedEvent.scheduled_at), 'dd MMMM yyyy', { locale: tr })}</span>
                      </div>
                    </div>
                    <div className="detail-item">
                      <span className="detail-icon">⏰</span>
                      <div>
                        <span className="detail-label">Saat</span>
                        <span className="detail-value">{format(new Date(selectedEvent.scheduled_at), 'HH:mm')}</span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-price">
                    <span>Tutar</span>
                    <span className="price-value">{selectedEvent.final_price} ₺</span>
                  </div>

                  {selectedEvent.notes && (
                    <div style={{ marginTop: '12px', padding: '10px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-muted)' }}>Müşteri Notu:</span>
                      <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text)' }}>{selectedEvent.notes}</p>
                    </div>
                  )}

                  {selectedEvent.internal_notes && (
                    <div style={{ marginTop: '12px', padding: '10px', background: 'rgba(245,158,11,0.05)', borderRadius: '8px', borderLeft: '3px solid #f59e0b' }}>
                      <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#f59e0b' }}>Dahili Not:</span>
                      <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text)' }}>{selectedEvent.internal_notes}</p>
                    </div>
                  )}

                  {selectedEvent.rating && (
                    <div style={{ marginTop: '12px', padding: '10px', background: 'rgba(16,185,129,0.05)', borderRadius: '8px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#10b981' }}>Değerlendirme:</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', margin: '4px 0' }}>
                        {Array.from({ length: 5 }).map((_, idx) => (
                          <span key={idx} style={{ color: idx < selectedEvent.rating ? '#fbbf24' : 'rgba(255,255,255,0.2)', fontSize: '16px' }}>★</span>
                        ))}
                      </div>
                      {selectedEvent.review_text && (
                        <p style={{ margin: '4px 0 0', fontSize: '12.5px', fontStyle: 'italic', color: 'var(--text-muted)' }}>"{selectedEvent.review_text}"</p>
                      )}
                    </div>
                  )}

                  {selectedEvent.extras && selectedEvent.extras.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-muted)' }}>Ekstralar / Ek Hizmetler:</span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
                        {selectedEvent.extras.map(ex => (
                          <div key={ex.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'rgba(255,255,255,0.02)', borderRadius: '6px', fontSize: '12.5px' }}>
                            <span>✨ {ex.service_name}</span>
                            <span style={{ fontWeight: 'bold' }}>{ex.price} ₺</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedEvent.status === 'completed' && (
                    <div style={{ marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '12px' }}>
                      <span style={{ fontSize: '12.5px', fontWeight: 'bold', color: 'var(--text-muted)' }}>Müşteri Değerlendirmesi & Notlar</span>
                      
                      <div className="grid grid-2" style={{ marginTop: '8px', gap: '10px' }}>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label" style={{ fontSize: '11px', marginBottom: '4px' }}>Puan (1-5)</label>
                          <select 
                            className="select" 
                            value={selectedEvent.rating || ''} 
                            onChange={async (e) => {
                              const val = parseInt(e.target.value, 10) || null;
                              await handleUpdateFields({ rating: val });
                            }}
                            style={{ padding: '6px 10px', fontSize: '13px' }}
                          >
                            <option value="">Seçin</option>
                            <option value="1">1 Yıldız</option>
                            <option value="2">2 Yıldız</option>
                            <option value="3">3 Yıldız</option>
                            <option value="4">4 Yıldız</option>
                            <option value="5">5 Yıldız</option>
                          </select>
                        </div>

                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label" style={{ fontSize: '11px', marginBottom: '4px' }}>Müşteri Yorumu</label>
                          <input 
                            type="text" 
                            className="input" 
                            placeholder="Müşteri yorumu..." 
                            value={selectedEvent.review_text || ''} 
                            onBlur={async (e) => {
                              await handleUpdateFields({ review_text: e.target.value });
                            }}
                            onChange={(e) => setSelectedEvent({ ...selectedEvent, review_text: e.target.value })}
                            style={{ padding: '6px 10px', fontSize: '13px' }}
                          />
                        </div>
                      </div>

                      <div className="form-group" style={{ marginTop: '8px' }}>
                        <label className="form-label" style={{ fontSize: '11px', marginBottom: '4px' }}>Dahili Not (Sadece Personel)</label>
                        <textarea 
                          className="textarea" 
                          placeholder="Randevu hakkında dahili notlar..." 
                          value={selectedEvent.internal_notes || ''} 
                          onBlur={async (e) => {
                            await handleUpdateFields({ internal_notes: e.target.value });
                          }}
                          onChange={(e) => setSelectedEvent({ ...selectedEvent, internal_notes: e.target.value })}
                          rows={2}
                          style={{ padding: '8px', fontSize: '13px' }}
                        />
                      </div>
                    </div>
                  )}

                  {customerHistory && customerHistory.filter(h => h.id !== selectedEvent.id).length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-muted)' }}>🕘 Geçmiş Randevular:</span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
                        {customerHistory.filter(h => h.id !== selectedEvent.id).slice(0, 5).map(h => (
                          <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', padding: '6px 10px', background: 'rgba(255,255,255,0.02)', borderRadius: '6px', fontSize: '12.5px' }}>
                            <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{format(new Date(h.scheduled_at), 'dd MMM yyyy', { locale: tr })}</span>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.service_name}</span>
                            <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{h.staff_name}</span>
                            <span className={`status-badge status-${h.status}`} style={{ padding: '2px 8px', fontSize: '10.5px', flexShrink: 0 }}>
                              {{ pending: 'Bekliyor', confirmed: 'Onaylı', in_progress: 'İşlemde', completed: 'Tamamlandı', cancelled_by_customer: 'İptal', cancelled_by_salon: 'İptal', no_show: 'Gelmedi' }[h.status] || h.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="status-actions" style={{ marginTop: '16px' }}>
                    <span className="action-label">İşlemler</span>
                    <div className="action-buttons">
                      {(isFullAccess || (selectedEvent.staff_id === user?.id && appointmentPerms.canEdit)) && (
                        <button className="btn btn-secondary" type="button" onClick={() => {
                          setNewAppointment({
                            customer_id: selectedEvent.customer_id,
                            staff_id: selectedEvent.staff_id || '',
                            service_id: selectedEvent.service_id || '',
                            product_id: selectedEvent.product_id || '',
                            fitting_type: selectedEvent.fitting_type || '',
                            scheduled_at: format(new Date(selectedEvent.scheduled_at), "yyyy-MM-dd'T'HH:mm"),
                            notes: selectedEvent.notes || ''
                          });
                          setServiceRows([{ service_id: selectedEvent.service_id || '', staff_id: isFullAccess ? (selectedEvent.staff_id || '') : user?.id }]);
                          setBatchConflictIndex(null);
                          setCustomerSearchQuery(`${selectedEvent.customer_first_name || ''} ${selectedEvent.customer_last_name || ''}`.trim());
                          setCustomerSearchResults([]);
                          setIsEditing(true);
                        }}>✍ Düzenle</button>
                      )}
                      {selectedEvent.status === 'pending' && (
                        <button className="btn btn-primary" onClick={() => handleUpdateStatus('confirmed')}>✓ Onayla</button>
                      )}
                      {selectedEvent.status === 'confirmed' && (
                        <button className="btn btn-secondary" onClick={() => handleUpdateStatus('in_progress')}>▶ İşleme Başla</button>
                      )}
                      {selectedEvent.status === 'in_progress' && (
                        <button className="btn btn-success" onClick={() => handleUpdateStatus('completed')}>✓ Tamamla</button>
                      )}
                      {['pending', 'confirmed', 'in_progress'].includes(selectedEvent.status) &&
                        (isFullAccess || (selectedEvent.staff_id === user?.id && appointmentPerms.canDelete)) && (
                        <button className="btn btn-danger" onClick={async () => {
                          const reason = window.prompt('İptal nedenini girin (isteğe bağlı):');
                          if (reason === null) return;
                          try {
                            const token = localStorage.getItem('crm_token');
                            await axios.delete(`/api/appointments/${selectedEvent.id}`, {
                              data: { reason, cancelled_by: 'salon' },
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            setShowModal(false);
                            fetchAppointments();
                          } catch (err) {
                            alert('İptal işlemi başarısız: ' + (err.response?.data?.error || err.message));
                          }
                        }}>✕ İptal Et</button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <form className="modal-body" onSubmit={handleSaveAppointment}>
                {preConsultationId && !isEditing && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '14px', padding: '8px 12px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '10px', fontSize: '12.5px', color: '#34d399' }}>
                    <span>📋 Ön görüşmeden: <strong>{preConsultationName}</strong> — kayıtta talep "Randevulu" olacak</span>
                    <button
                      type="button"
                      title="Ön görüşme bağını kaldır"
                      onClick={() => { setPreConsultationId(null); setPreConsultationName(''); }}
                      style={{ background: 'none', border: 'none', color: '#34d399', cursor: 'pointer', fontSize: '14px', padding: 0 }}
                    >✕</button>
                  </div>
                )}
                <div className="form-group" style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label className="form-label">Müşteri *</label>
                    <button type="button" onClick={() => setShowNewCustomerModal(true)} style={{ background: 'none', border: 'none', color: 'var(--tertiary)', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>+ Yeni Ekle</button>
                  </div>
                  <input
                    type="text"
                    className="input"
                    placeholder="İsim veya telefon ile ara..."
                    value={customerSearchQuery}
                    onChange={e => {
                      setCustomerSearchQuery(e.target.value);
                      setShowCustomerDropdown(true);
                      if (newAppointment.customer_id) setNewAppointment({ ...newAppointment, customer_id: '' });
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 150)}
                    autoComplete="off"
                  />
                  {showCustomerDropdown && customerSearchQuery.trim().length >= 2 && (
                    <div className="customer-dropdown">
                      {customerSearchResults.length > 0 ? (
                        customerSearchResults.map(c => (
                          <div
                            key={c.id}
                            className="customer-dropdown-item"
                            onMouseDown={() => {
                              setNewAppointment({ ...newAppointment, customer_id: c.id });
                              setCustomerSearchQuery(`${c.first_name} ${c.last_name || ''} - ${c.phone}`);
                              setShowCustomerDropdown(false);
                            }}
                          >
                            <span className="customer-dropdown-name">{c.first_name} {c.last_name}</span>
                            <span className="customer-dropdown-phone">{c.phone}</span>
                          </div>
                        ))
                      ) : (
                        <div className="customer-dropdown-empty">Sonuç bulunamadı</div>
                      )}
                    </div>
                  )}
                </div>

                {industry !== 'bridal_shop' && newAppointment.customer_id && customerHistory && customerHistory.length > 0 && (
                  <div style={{ marginBottom: '14px', padding: '10px 12px', background: 'rgba(var(--tertiary-rgb),0.05)', border: '1px solid rgba(var(--tertiary-rgb),0.15)', borderRadius: '10px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-muted)' }}>🕘 Son işlemler (tıklayınca hizmet seçilir):</span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px', maxHeight: '130px', overflowY: 'auto' }}>
                      {customerHistory.slice(0, 5).map(h => (
                        <div
                          key={h.id}
                          onClick={() => {
                            if (!h.service_id) return;
                            setServiceRows(prev => [{ ...prev[0], service_id: h.service_id }, ...prev.slice(1)]);
                            const s = services.find(sv => sv.id === h.service_id);
                            if (s) toast.success(`Hizmet seçildi: ${s.name}`);
                          }}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', padding: '5px 8px', background: 'rgba(255,255,255,0.02)', borderRadius: '6px', fontSize: '12px', cursor: h.service_id ? 'pointer' : 'default' }}
                        >
                          <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{format(new Date(h.scheduled_at), 'dd MMM yyyy', { locale: tr })}</span>
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.service_name}</span>
                          <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{h.staff_name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {industry !== 'bridal_shop' ? (
                  <>
                    {serviceRows.map((row, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          gap: '10px',
                          alignItems: 'flex-end',
                          ...(batchConflictIndex === idx ? { outline: '1px solid #ef4444', borderRadius: '10px', padding: '6px', margin: '-6px -6px 6px' } : {})
                        }}
                      >
                        <div className="form-group" style={{ flex: 1 }}>
                          {idx === 0 && <label className="form-label">{labels.service} *</label>}
                          <select
                            className="select"
                            value={row.service_id}
                            onChange={e => {
                              const v = e.target.value;
                              setServiceRows(prev => prev.map((r, i) => i === idx ? { ...r, service_id: v } : r));
                              setBatchConflictIndex(null);
                            }}
                            required
                          >
                            <option value="">{labels.service} seçin</option>
                            {services.map(s => (<option key={s.id} value={s.id}>{s.name} - {s.price}₺ ({s.duration_min} dk)</option>))}
                          </select>
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                          {idx === 0 && <label className="form-label">{labels.staff} *</label>}
                          {isFullAccess ? (
                            <select
                              className="select"
                              value={row.staff_id}
                              onChange={e => {
                                const v = e.target.value;
                                setServiceRows(prev => prev.map((r, i) => i === idx ? { ...r, staff_id: v } : r));
                                setBatchConflictIndex(null);
                              }}
                              required
                            >
                              <option value="">{labels.staff} seçin</option>
                              {staff.map(s => (<option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>))}
                            </select>
                          ) : (
                            <input className="input" type="text" disabled value={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()} />
                          )}
                        </div>
                        {idx > 0 && (
                          <button
                            type="button"
                            title="Bu hizmeti kaldır"
                            onClick={() => { setServiceRows(prev => prev.filter((_, i) => i !== idx)); setBatchConflictIndex(null); }}
                            style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', borderRadius: '8px', width: '36px', height: '38px', cursor: 'pointer', flexShrink: 0, marginBottom: '16px' }}
                          >✕</button>
                        )}
                      </div>
                    ))}
                    {!isEditing && (
                      <button
                        type="button"
                        onClick={() => setServiceRows(prev => [...prev, { service_id: '', staff_id: prev[prev.length - 1]?.staff_id || '' }])}
                        style={{ background: 'none', border: 'none', color: 'var(--tertiary)', fontSize: '13px', cursor: 'pointer', fontWeight: 600, padding: 0, marginBottom: '12px' }}
                      >
                        + Hizmet Ekle (peş peşe ayrı randevu oluşturulur)
                      </button>
                    )}
                    {chainSummary && (
                      <div style={{ marginBottom: '12px', padding: '8px 12px', background: 'rgba(var(--tertiary-rgb),0.06)', border: '1px solid rgba(var(--tertiary-rgb),0.2)', borderRadius: '8px', fontSize: '12.5px', color: 'var(--text)' }}>
                        Toplam: <strong>{chainSummary.count} hizmet · {chainSummary.totalMin} dk · {format(chainSummary.start, 'HH:mm')}–{format(chainSummary.end, 'HH:mm')} · {chainSummary.totalPrice.toLocaleString('tr-TR')}₺</strong>
                        {chainSummary.crossesMidnight && (
                          <div style={{ color: '#fbbf24', marginTop: '4px' }}>⚠ Hizmet zinciri gece yarısını aşıyor — başlangıç saatini öne alın veya hizmet azaltın.</div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label className="form-label">{labels.service} *</label>
                      <select className="select" value={newAppointment.product_id || ''} onChange={e => setNewAppointment({ ...newAppointment, product_id: e.target.value })} required>
                        <option value="">{labels.service} seçin</option>
                        {services.map(p => (<option key={p.id} value={p.id}>{p.name} - {p.price}₺</option>))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Prova Türü *</label>
                      <select className="select" value={newAppointment.fitting_type || ''} onChange={e => setNewAppointment({ ...newAppointment, fitting_type: e.target.value })} required>
                        <option value="">Prova türü seçin</option>
                        <option value="first_viewing">İlk Görüntüleme</option>
                        <option value="measurement">Ölçü Alma</option>
                        <option value="first_fitting">1. Prova</option>
                        <option value="second_fitting">2. Prova</option>
                        <option value="third_fitting">3. Prova</option>
                        <option value="final_fitting">Son Prova</option>
                        <option value="delivery">Teslimat</option>
                      </select>
                    </div>
                  </>
                )}
                
                <div className="form-group">
                  <label className="form-label">Tarih ve Saat *</label>
                  <input type="datetime-local" className="input" value={newAppointment.scheduled_at} onChange={e => setNewAppointment({ ...newAppointment, scheduled_at: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Not</label>
                  <textarea className="textarea" value={newAppointment.notes} onChange={e => setNewAppointment({ ...newAppointment, notes: e.target.value })} placeholder="Randevu notu..." rows={3} />
                </div>
                <div className="modal-footer" style={{ padding: 0, border: 'none', background: 'transparent', marginTop: '8px' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setIsEditing(false); }}>İptal</button>
                  <button type="submit" className="btn btn-primary">{isEditing ? 'Değişiklikleri Kaydet' : 'Randevu Oluştur'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* QUICK ADD CUSTOMER MODAL */}
      {showNewCustomerModal && (
        <div className="modal-overlay appointment-modal-overlay" style={{ zIndex: 10001, ...(isMobile ? { alignItems: 'flex-end', padding: 0 } : {}) }}>
          <div className="modal appointment-modal" style={isMobile ? { maxWidth: '100%', width: '100%', borderRadius: '20px 20px 0 0', maxHeight: '92dvh', overflowY: 'auto', margin: 0 } : { maxWidth: '400px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Yeni Müşteri Ekle</h3>
              <button className="modal-close" onClick={() => setShowNewCustomerModal(false)}>✕</button>
            </div>
            <form className="modal-body" onSubmit={handleQuickAddCustomer}>
              <div className="form-group">
                <label className="form-label">Ad *</label>
                <input type="text" className="input" value={newCustomerForm.first_name} onChange={e => setNewCustomerForm({ ...newCustomerForm, first_name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Soyad</label>
                <input type="text" className="input" value={newCustomerForm.last_name} onChange={e => setNewCustomerForm({ ...newCustomerForm, last_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Telefon *</label>
                <input type="text" className="input" value={newCustomerForm.phone} onChange={e => setNewCustomerForm({ ...newCustomerForm, phone: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">E-posta</label>
                <input type="email" className="input" value={newCustomerForm.email} onChange={e => setNewCustomerForm({ ...newCustomerForm, email: e.target.value })} />
              </div>
              <div className="modal-footer" style={{ padding: 0, border: 'none', background: 'transparent', marginTop: '16px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowNewCustomerModal(false)}>İptal</button>
                <button type="submit" className="btn btn-primary" disabled={customerSaving}>
                  {customerSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* BLOCK TIME MODAL */}
      {showBlockModal && (
        <div className="modal-overlay appointment-modal-overlay" style={{ zIndex: 10001, ...(isMobile ? { alignItems: 'flex-end', padding: 0 } : {}) }} onClick={() => setShowBlockModal(false)}>
          <div className="modal appointment-modal" style={isMobile ? { maxWidth: '100%', width: '100%', borderRadius: '20px 20px 0 0', maxHeight: '92dvh', overflowY: 'auto', margin: 0 } : { maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Saat Kapat</h3>
              <button className="modal-close" onClick={() => setShowBlockModal(false)}>✕</button>
            </div>
            <form className="modal-body" onSubmit={handleCreateBlock}>
              <div className="form-group">
                <label className="form-label">{labels.staff}</label>
                <select className="select" value={newBlock.staff_id} onChange={e => setNewBlock({ ...newBlock, staff_id: e.target.value })}>
                  <option value="">Tüm Personel (Şube geneli kapalı)</option>
                  {staff.map(s => (<option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>))}
                </select>
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Başlangıç *</label>
                  <input type="datetime-local" className="input" value={newBlock.start_at} onChange={e => setNewBlock({ ...newBlock, start_at: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Bitiş *</label>
                  <input type="datetime-local" className="input" value={newBlock.end_at} onChange={e => setNewBlock({ ...newBlock, end_at: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Sebep</label>
                <input type="text" className="input" placeholder="Örn: İzin, Mola, Bakım..." value={newBlock.reason} onChange={e => setNewBlock({ ...newBlock, reason: e.target.value })} />
              </div>
              <div className="modal-footer" style={{ padding: 0, border: 'none', background: 'transparent', marginTop: '16px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowBlockModal(false)}>İptal</button>
                <button type="submit" className="btn btn-primary" disabled={blockSaving}>
                  {blockSaving ? 'Kaydediliyor...' : 'Saati Kapat'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .modern-calendar {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: var(--radius-2xl);
          box-shadow: var(--shadow-xl);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          overflow: hidden;
        }

        .calendar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid var(--border);
          background: rgba(255, 255, 255, 0.01);
        }

        .calendar-nav {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .nav-btn {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          cursor: pointer;
          color: var(--text-muted);
          transition: var(--transition-fast);
        }

        .nav-btn:hover {
          background: rgba(255, 255, 255, 0.08);
          color: var(--text);
          border-color: var(--border-hover);
        }

        .nav-btn svg {
          width: 20px;
          height: 20px;
        }

        .today-btn {
          padding: 10px 20px;
          background: linear-gradient(135deg, var(--primary), var(--secondary));
          color: var(--bg);
          border: none;
          border-radius: var(--radius-md);
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
          transition: var(--transition-fast);
        }

        .today-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 0 15px rgba(var(--primary-rgb), 0.4);
          opacity: 0.95;
        }

        .calendar-title {
          font-size: 20px;
          font-weight: 700;
          color: var(--text);
          margin-left: 8px;
          text-transform: capitalize;
        }

        .view-tabs {
          display: flex;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          padding: 4px;
        }

        .view-tab {
          padding: 10px 20px;
          border: none;
          background: transparent;
          color: var(--text-muted);
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          border-radius: 8px;
          transition: var(--transition-fast);
        }

        .view-tab:hover {
          color: var(--text);
        }

        .view-tab.active {
          background: rgba(255, 255, 255, 0.08);
          color: var(--primary);
          box-shadow: none;
        }

        .weekday-header {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          background: rgba(255, 255, 255, 0.01);
          border-bottom: 1px solid var(--border);
        }

        .weekday {
          padding: 14px;
          text-align: center;
          font-weight: 600;
          font-size: 13px;
          color: var(--text-muted);
          text-transform: uppercase;
        }

        .month-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          min-height: 600px;
          background: rgba(0, 0, 0, 0.1);
        }

        .month-day {
          min-height: 120px;
          padding: 8px;
          border-right: 1px solid var(--border);
          border-bottom: 1px solid var(--border);
          cursor: pointer;
          transition: var(--transition-fast);
        }

        .month-day:hover {
          background: rgba(255, 255, 255, 0.02);
        }

        .month-day:nth-child(7n) {
          border-right: none;
        }

        .month-day.other-month {
          background: rgba(255, 255, 255, 0.01);
          opacity: 0.4;
        }

        .month-day.other-month .day-number {
          color: var(--text-muted);
        }

        .month-day.today {
          background: rgba(var(--primary-rgb), 0.05);
          border-color: rgba(var(--primary-rgb), 0.2);
        }

        .month-day.today .day-number {
          background: linear-gradient(135deg, var(--primary), var(--secondary));
          box-shadow: 0 0 10px rgba(var(--primary-rgb), 0.4);
          color: var(--bg);
          border-radius: 50%;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .day-number {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-muted);
          margin-bottom: 8px;
        }

        .day-events {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .day-event {
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 600;
          border-left: 3px solid;
          cursor: pointer;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          transition: var(--transition-fast);
        }

        .day-event:hover {
          transform: translateY(-1px);
          filter: brightness(1.1);
        }

        .event-time {
          font-weight: 700;
          margin-right: 4px;
        }

        .event-name {
          font-weight: 500;
        }

        .more-events {
          font-size: 11px;
          color: var(--primary);
          font-weight: 600;
          cursor: pointer;
          margin-top: 2px;
        }

        .time-grid {
          display: flex;
          min-height: 700px;
          overflow-x: auto;
        }

        .time-column {
          width: 70px;
          flex-shrink: 0;
          border-right: 1px solid var(--border);
        }

        .time-slot {
          height: ${SLOT_PX * 2}px;
          padding: 8px 12px;
          font-size: 12px;
          font-weight: 500;
          color: var(--text-muted);
          text-align: right;
        }

        .day-column {
          flex: 1;
          border-right: 1px solid var(--border);
        }

        .day-column:last-child {
          border-right: none;
        }

        .day-column.today {
          background: rgba(var(--primary-rgb), 0.03);
        }

        .day-header {
          padding: 12px;
          text-align: center;
          border-bottom: 1px solid var(--border);
          background: rgba(255, 255, 255, 0.02);
        }

        .day-name {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: var(--text-muted);
          text-transform: uppercase;
        }

        .day-date {
          display: block;
          font-size: 20px;
          font-weight: 700;
          color: var(--text);
          margin-top: 4px;
        }

        .day-column.today .day-date {
          color: var(--primary);
        }

        .day-slots {
          position: relative;
        }

        .half-slot {
          height: ${SLOT_PX}px;
          border-bottom: 1px dashed rgba(255, 255, 255, 0.05);
          cursor: pointer;
          transition: background 0.15s;
          box-sizing: border-box;
        }

        .half-slot.hour-end {
          border-bottom: 1px solid var(--border);
        }

        .half-slot:hover {
          background: rgba(255, 255, 255, 0.03);
        }

        .half-slot.drop-hover {
          background: rgba(var(--primary-rgb), 0.12);
          box-shadow: inset 0 0 0 1px rgba(var(--primary-rgb), 0.4);
        }

        /* Salon Görünümü'ne özel açık/krem tema - referans tasarımla birebir eşleşsin diye
           SADECE bu görünümdeki takvim gövdesini kaplar, uygulamanın geri kalanına (Orijinal
           Görünüm dahil) hiç sızmaz. */
        .salon-theme {
          background: #efe9d1;
        }

        .salon-theme .time-column {
          background: #efe9d1;
          border-right-color: rgba(0, 0, 0, 0.15);
        }

        .salon-theme .time-slot {
          color: #6b6555;
        }

        .salon-theme .day-column {
          border-right-color: rgba(0, 0, 0, 0.12);
        }

        .salon-theme .day-column.today {
          background: transparent;
        }

        .salon-theme .half-slot {
          border-bottom-color: rgba(0, 0, 0, 0.08);
        }

        .salon-theme .half-slot.hour-end {
          border-bottom-color: rgba(0, 0, 0, 0.18);
        }

        .salon-theme .half-slot:hover {
          background: rgba(0, 0, 0, 0.04);
        }

        .block-overlay {
          position: absolute;
          left: 0;
          right: 0;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 600;
          color: #f87171;
          text-align: center;
          padding: 2px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          cursor: not-allowed;
          background: repeating-linear-gradient(
            135deg,
            rgba(239, 68, 68, 0.06),
            rgba(239, 68, 68, 0.06) 8px,
            rgba(239, 68, 68, 0.1) 8px,
            rgba(239, 68, 68, 0.1) 16px
          );
          border-top: 1px dashed rgba(239, 68, 68, 0.4);
          border-bottom: 1px dashed rgba(239, 68, 68, 0.4);
          box-sizing: border-box;
        }

        .block-overlay:hover {
          background: repeating-linear-gradient(
            135deg,
            rgba(239, 68, 68, 0.1),
            rgba(239, 68, 68, 0.1) 8px,
            rgba(239, 68, 68, 0.16) 8px,
            rgba(239, 68, 68, 0.16) 16px
          );
        }

        /* Salon Görünümü'ne özel - Orijinal Görünüm yukarıdaki eski (taralı kırmızı) stili korur */
        .block-overlay-salon {
          align-items: flex-start;
          justify-content: flex-start;
          color: rgba(255, 255, 255, 0.4);
          text-align: left;
          padding: 6px 8px;
          background: #0a0a0a;
          border-top: none;
          border-bottom: none;
        }

        .block-overlay-salon:hover {
          background: #141414;
        }

        .current-time-line {
          position: absolute;
          left: 0;
          right: 0;
          height: 2px;
          background: #ef4444;
          z-index: 5;
          pointer-events: none;
        }

        .current-time-line::before {
          content: '';
          position: absolute;
          left: 0;
          top: -4px;
          width: 0;
          height: 0;
          border-top: 5px solid transparent;
          border-bottom: 5px solid transparent;
          border-left: 6px solid #ef4444;
        }

        .events-layer {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 3;
        }

        .calendar-event {
          position: absolute;
          pointer-events: auto;
          min-width: 0;
          padding: 3px 8px;
          border-radius: 8px;
          font-size: 11px;
          cursor: pointer;
          border-left-width: 4px;
          border-left-style: solid;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          transition: box-shadow 0.2s, filter 0.2s;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          box-sizing: border-box;
        }

        .calendar-event:hover {
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
          filter: brightness(1.15);
          z-index: 10;
        }

        .calendar-event[draggable="true"] {
          cursor: grab;
        }

        .calendar-event.cancelled {
          opacity: 0.55;
          border-left-style: dashed;
        }

        .calendar-event.cancelled .event-title {
          text-decoration: line-through;
        }

        .event-content {
          display: flex;
          flex-direction: column;
          gap: 1px;
          overflow: hidden;
          width: 100%;
          min-width: 0;
        }

        .calendar-event .event-time,
        .calendar-event .event-title,
        .calendar-event .event-service {
          display: block;
          width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          line-height: 1.25;
        }

        .calendar-event .event-time {
          font-weight: 700;
          font-size: 10.5px;
          opacity: 0.9;
        }

        .calendar-event .event-title {
          font-weight: 700;
          font-size: 12px;
          color: inherit;
        }

        .calendar-event .event-service {
          font-size: 10.5px;
          opacity: 0.85;
        }

        .appointment-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 16px 24px;
        }

        .appointment-list-empty {
          padding: 40px 0;
          text-align: center;
          color: var(--text-muted);
          font-size: 14px;
        }

        .appointment-list-row {
          display: grid;
          grid-template-columns: 90px 1fr 180px 110px 90px;
          align-items: center;
          gap: 16px;
          padding: 12px 16px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid var(--border);
          border-left: 4px solid transparent;
          cursor: pointer;
          transition: background 0.15s, border-color 0.15s;
        }

        .appointment-list-row:hover {
          background: rgba(255, 255, 255, 0.05);
        }

        .appointment-list-row.cancelled {
          opacity: 0.55;
        }

        .appointment-list-row.cancelled .al-customer {
          text-decoration: line-through;
        }

        .al-time {
          display: flex;
          flex-direction: column;
          line-height: 1.3;
        }

        .al-time-start {
          font-size: 15px;
          font-weight: 700;
          color: var(--text);
        }

        .al-time-end {
          font-size: 11px;
          color: var(--text-muted);
        }

        .al-main {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }

        .al-customer {
          font-size: 14px;
          font-weight: 600;
          color: var(--text);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .al-service {
          font-size: 12.5px;
          color: var(--text-muted);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .al-staff {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: var(--text-muted);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .al-staff-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .al-status {
          font-size: 11.5px;
          font-weight: 700;
          text-align: center;
          padding: 5px 10px;
          border-radius: 20px;
          border: 1px solid;
          white-space: nowrap;
        }

        .al-price {
          font-size: 13.5px;
          font-weight: 700;
          color: var(--text);
          text-align: right;
        }

        @media (max-width: 900px) {
          .appointment-list-row {
            grid-template-columns: 70px 1fr 90px;
          }
          .al-staff, .al-price {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            gap: 8px;
            margin-bottom: 12px;
          }

          .page-header .page-title {
            font-size: 20px;
          }

          .page-header .page-subtitle {
            display: none;
          }

          .calendar-header {
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
            padding: 10px 12px;
          }

          .calendar-nav {
            justify-content: space-between;
            width: 100%;
          }

          .calendar-title {
            font-size: 14px;
            font-weight: 700;
          }

          .nav-btn, .today-btn {
            padding: 4px 10px;
            font-size: 12px;
          }

          .view-tabs {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            width: 100%;
            justify-content: flex-start;
            padding-bottom: 2px;
            gap: 4px;
          }

          .view-tabs::-webkit-scrollbar {
            display: none;
          }

          .view-tab {
            white-space: nowrap;
            padding: 6px 12px;
            font-size: 12px;
          }

          /* Gün/Hafta Görünümü - Sıkışmayı Önleyen Yatay Kaydırma */
          .time-grid {
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch;
            scroll-behavior: smooth;
            width: 100%;
            position: relative;
          }

          .time-column {
            width: 46px !important;
            position: sticky !important;
            left: 0 !important;
            z-index: 25 !important;
            background: var(--bg-card, #0c0f19) !important;
            box-shadow: 2px 0 8px rgba(0, 0, 0, 0.45);
          }

          .salon-theme .time-column {
            background: #efe9d1 !important;
          }

          .time-slot {
            font-size: 10.5px !important;
            padding: 0 4px !important;
            text-align: center;
          }

          .day-column {
            min-width: 220px !important;
            flex-shrink: 0;
            border-right: 1px solid var(--border);
          }

          .day-header {
            position: sticky;
            top: 0;
            z-index: 15;
            padding: 8px 10px !important;
          }

          .day-header .day-name {
            font-size: 12.5px !important;
          }

          .calendar-event {
            padding: 3px 6px;
            font-size: 10.5px;
          }

          .calendar-event .event-title {
            font-size: 11px;
          }

          /* Mobil Liste Görünümü: Satırdan Karta Dönüşüm */
          .appointment-list {
            padding: 10px;
            gap: 10px;
          }

          .appointment-list-row {
            grid-template-columns: 1fr !important;
            gap: 8px;
            padding: 14px;
            border-radius: 14px;
          }

          .al-time {
            flex-direction: row;
            gap: 8px;
            align-items: baseline;
          }

          .al-time-start {
            font-size: 16px;
            font-weight: 800;
          }

          .al-staff {
            display: flex !important;
            font-size: 12px;
            color: var(--text-muted);
          }

          .al-price {
            display: block !important;
            text-align: left;
            font-size: 13px;
          }

          .al-status {
            align-self: flex-start;
          }

          /* Mobil Modal - Bottom Sheet */
          .appointment-modal-overlay {
            align-items: flex-end !important;
            padding: 0 !important;
          }

          .appointment-modal {
            border-radius: 20px 20px 0 0 !important;
            max-height: 92dvh !important;
            max-width: 100% !important;
            width: 100% !important;
            margin: 0 !important;
            overflow-y: auto;
            animation: slideUpCalModal 0.28s cubic-bezier(0.16, 1, 0.3, 1);
          }

          @keyframes slideUpCalModal {
            from { transform: translateY(100%); }
            to   { transform: translateY(0); }
          }
        }

        @media (max-width: 480px) {
          .appointment-list {
            padding: 8px;
          }

          .appointment-list-row {
            padding: 12px !important;
          }
        }

        .customer-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          margin-top: 4px;
          max-height: 240px;
          overflow-y: auto;
          background: var(--bg-card, #16171f);
          border: 1px solid var(--border);
          border-radius: 10px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
          z-index: 20;
        }

        .customer-dropdown-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
          padding: 9px 12px;
          cursor: pointer;
          font-size: 13px;
        }

        .customer-dropdown-item:hover {
          background: rgba(var(--tertiary-rgb), 0.12);
        }

        .customer-dropdown-name {
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .customer-dropdown-phone {
          font-size: 12px;
          color: var(--text-muted);
          flex-shrink: 0;
        }

        .customer-dropdown-empty {
          padding: 10px 12px;
          font-size: 13px;
          color: var(--text-muted);
        }

        .detail-header {
          display: flex;
          align-items: center;
          gap: 16px;
          padding-bottom: 20px;
          border-bottom: 1px solid var(--border);
        }

        .detail-header-info {
          flex: 1;
        }

        .detail-header-info h3 {
          font-size: 18px;
          font-weight: 700;
          color: var(--text);
          margin: 0;
        }

        .detail-header-info p {
          font-size: 14px;
          color: var(--text-muted);
          margin: 4px 0 0;
        }

        .status-badge {
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
        }

        .status-pending { background: rgba(245, 158, 11, 0.15); color: #fbbf24; border: 1px solid rgba(245, 158, 11, 0.3); }
        .status-confirmed { background: rgba(59, 130, 246, 0.15); color: #60a5fa; border: 1px solid rgba(59, 130, 246, 0.3); }
        .status-in_progress { background: rgba(var(--secondary-rgb), 0.15); color: #a78bfa; border: 1px solid rgba(var(--secondary-rgb), 0.3); }
        .status-completed { background: rgba(16, 185, 129, 0.15); color: #34d399; border: 1px solid rgba(16, 185, 129, 0.3); }
        .status-cancelled { background: rgba(239, 68, 68, 0.15); color: #f87171; border: 1px solid rgba(239, 68, 68, 0.3); }
        .status-cancelled_by_customer { background: rgba(239, 68, 68, 0.15); color: #f87171; border: 1px solid rgba(239, 68, 68, 0.3); }
        .status-cancelled_by_salon { background: rgba(239, 68, 68, 0.15); color: #f87171; border: 1px solid rgba(239, 68, 68, 0.3); }
        .status-no_show { background: rgba(107, 114, 128, 0.15); color: #9ca3af; border: 1px solid rgba(107, 114, 128, 0.3); }

        .detail-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-top: 20px;
        }

        .detail-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid var(--border);
          border-radius: var(--radius);
        }

        .detail-icon {
          font-size: 20px;
        }

        .detail-item .detail-label {
          font-size: 11px;
          color: var(--text-muted);
          display: block;
        }

        .detail-item .detail-value {
          font-size: 14px;
          font-weight: 500;
          color: var(--text);
          display: block;
        }

        .detail-price {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          background: linear-gradient(135deg, var(--primary), var(--secondary));
          border-radius: 12px;
          color: var(--bg);
          margin-top: 20px;
        }

        .detail-price span:first-child {
          font-size: 14px;
          opacity: 0.9;
          font-weight: 600;
        }

        .price-value {
          font-size: 24px;
          font-weight: 700;
        }

        .status-actions {
          padding-top: 16px;
          border-top: 1px solid var(--border);
          margin-top: 20px;
        }

        .action-label {
          font-size: 12px;
          color: var(--text-muted);
          font-weight: 600;
          display: block;
          margin-bottom: 12px;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
      `}</style>
    </div>
  );
}

export default Calendar;
